import vars from "utils/vars";

export const getAppointmentTypeValue = (appointment = {}) => {
	const type = appointment?.type;

	if (type && typeof type === "object") {
		return type?.value || "";
	}

	return type || "";
};

export const isPatientAppointmentGrouped = (appointment = {}) => {
	if (Boolean(+appointment?.is_grouped)) return true;

	return Number(appointment?.child_orders_count ?? 0) >= 2;
};

export const getPatientAppointmentChildOrderCounts = (appointment = {}) => ({
	completed: Number(appointment?.completed_child_orders_count ?? 0),
	total: Number(appointment?.child_orders_count ?? 0),
});

export const getPatientChildOrderStatusColor = (status = "") => {
	const normalizedStatus = String(status || "").toLowerCase();

	return vars.app.orderSubmissions.statusColors[normalizedStatus] || "";
};

export const formatPatientChildOrderDateTime = (childOrder = {}) => {
	const parts = [childOrder?.date, childOrder?.time].filter(Boolean);

	return parts.length ? parts.join(" - ") : "-";
};

export const canNavigateToPatientChildOrder = (childOrder = {}) =>
	Boolean(childOrder?.is_navigable && childOrder?.appointment_id);

export const canSendPatientAppointmentProviderRemark = (appointment = {}) =>
	getAppointmentTypeValue(appointment) === "lab_test" && Boolean(appointment?.note_id);

export const isPatientAppointmentFillInAllowed = (appointment = {}) => {
	const isQuestionnaire = Boolean(+appointment?.is_questionnaire);
	const isPatientIntake = Boolean(+appointment?.is_patient_intake);
	const allowPatientIntake = Boolean(+appointment?.allow_patient_intake);

	return (
		(isQuestionnaire || (!isPatientIntake && allowPatientIntake)) &&
		Boolean(appointment?.tenant_name) &&
		Boolean(appointment?.note_id)
	);
};

export const canPatientEditAppointment = (appointment = {}) => {
	if (!appointment?.can_edit) return false;

	const isLabTestFromHcp =
		getAppointmentTypeValue(appointment) === "lab_test" && Boolean(appointment?.note_id);

	if (isLabTestFromHcp) return false;

	return true;
};

/** Order-linked appointments (e.g. lab requisition) use Referral ID instead of Note ID. */
export const isPatientAppointmentOrderLinked = (appointment = {}) => {
	if (getAppointmentTypeValue(appointment) === "lab_test" && Boolean(appointment?.note_id)) return true;

	return (appointment?.attachments || []).some(
		(attachment) => String(attachment?.assessment_type || "").toLowerCase() === "order"
	);
};

export const hasPatientAppointmentActions = (appointment = {}) =>
	isPatientAppointmentFillInAllowed(appointment) || canPatientEditAppointment(appointment);

export const isPatientAuthoredAppointmentRemark = (remark = {}) => {
	const audience = String(remark?.audience || "").toLowerCase();

	if (audience === "provider") return true;
	if (audience === "internal") return true;

	return false;
};

export const shouldShowPatientAppointmentRemarkBadge = (remark = {}) =>
	!isPatientAuthoredAppointmentRemark(remark);

export const getAppointmentHealthcareName = (appointment = {}) =>
	appointment?.hcp_name || appointment?.clinic_name || "";

export const getAppointmentPhone = (appointment = {}) =>
	appointment?.phone || appointment?.clinic_phone || appointment?.business_phone || appointment?.hcp_phone || "";

export const getAppointmentDoctorName = (appointment = {}) =>
	appointment?.doctor || appointment?.attachments?.[0]?.tenant?.physician_name || "";

const normalizeRemarkDateKey = (createdAt) => {
	if (!createdAt) return "";
	try {
		return String(createdAt).slice(0, 10);
	} catch {
		return String(createdAt);
	}
};

/** Hide redundant internal copy when API duplicates provider-only patient remarks. */
export const shouldHideRedundantInternalAppointmentRemark = (remark, allRemarks = []) => {
	if (String(remark?.audience || "").toLowerCase() !== "internal") return false;

	const remarkText = (remark?.remarks || "").trim();
	const remarkDate = normalizeRemarkDateKey(remark?.created_at);

	return allRemarks.some(
		(other) =>
			other?.id !== remark?.id &&
			String(other?.audience || "").toLowerCase() === "provider" &&
			(other?.remarks || "").trim() === remarkText &&
			normalizeRemarkDateKey(other?.created_at) === remarkDate
	);
};

export const getPatientVisibleAppointmentRemarks = (remarks = []) => {
	const list = Array.isArray(remarks) ? remarks : [];

	return list
		.filter((remark) => !shouldHideRedundantInternalAppointmentRemark(remark, list))
		.sort((a, b) => {
			const dateA = new Date(a?.created_at || 0).getTime();
			const dateB = new Date(b?.created_at || 0).getTime();
			return dateA - dateB;
		});
};

export const getPatientAppointmentRemarkLabelKey = (remark = {}) => {
	const audience = String(remark?.audience || "").toLowerCase();

	if (audience === "patient" && remark?.can_reply) {
		return "appointment.remarks_label_from_provider";
	}
	if (audience === "provider" || audience === "internal") {
		return "";
	}
	if (audience === "patient") {
		return "requisition.remarks_audience_patient";
	}

	return `requisition.remarks_audience_${audience}`;
};

export const mapAppointmentRemarkToDisplayMessage = (remark = {}) => {
	const audience = String(remark?.audience || "patient").toLowerCase();

	return {
		id: remark?.id,
		type: audience,
		author: "",
		timestamp: remark?.created_at || "",
		content: remark?.remarks || "",
		canReply: Boolean(remark?.can_reply),
		replies: Array.isArray(remark?.replies)
			? remark.replies.map((reply) => ({
					id: reply?.id,
					type: String(reply?.audience || audience).toLowerCase(),
					author: "",
					timestamp: reply?.created_at || "",
					content: reply?.remarks || reply?.text || "",
					replies: [],
			  }))
			: [],
	};
};
